/**
 * useCalendarMemberMapping Hook
 *
 * Manages state for calendar-to-family-member mappings.
 * Provides methods to set and remove mappings.
 */

import { useState, useEffect, useCallback } from 'react';
import { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import { useCalendarMemberMappingStore } from './use-scoped-managers';
import { CalendarMappingEvents } from '@/lib/calendar/CalendarMappingEvents';

type UseCalendarMemberMappingResult = {
  mappings: CalendarMemberMapping[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  getMappingForCalendar: (calendarId: string) => CalendarMemberMapping | undefined;
  setMapping: (calendarId: string, memberIds: string[]) => Promise<void>;
  removeMapping: (calendarId: string) => Promise<void>;
  getMemberIdsForCalendar: (calendarId: string) => string[];
};

export function useCalendarMemberMapping(): UseCalendarMemberMappingResult {
  const store = useCalendarMemberMappingStore();
  const [mappings, setMappings] = useState<CalendarMemberMapping[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMappings = useCallback(async () => {
    if (!store) {
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const loadedMappings = await store.getAll();
      setMappings(loadedMappings);
    } catch (err) {
      console.error('Error loading calendar member mappings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load mappings');
    } finally {
      setIsLoading(false);
    }
  }, [store]);

  useEffect(() => {
    if (store) {
      loadMappings();
    }
  }, [store, loadMappings]);

  // Subscribe to calendar-member mapping changes from other screens
  useEffect(() => {
    const unsubscribe = CalendarMappingEvents.subscribe(() => {
      loadMappings();
    });
    return unsubscribe;
  }, [loadMappings]);

  const getMappingForCalendar = useCallback((calendarId: string): CalendarMemberMapping | undefined => {
    return mappings.find(m => m.calendarId === calendarId);
  }, [mappings]);

  const getMemberIdsForCalendar = useCallback((calendarId: string): string[] => {
    const mapping = mappings.find(m => m.calendarId === calendarId);
    return mapping?.memberIds || [];
  }, [mappings]);

  const setMapping = useCallback(async (calendarId: string, memberIds: string[]): Promise<void> => {
    if (!store) {
      throw new Error('Storage not ready');
    }
    const newMapping = await store.setMapping(calendarId, memberIds);
    setMappings(prev => {
      const existing = prev.findIndex(m => m.calendarId === calendarId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = newMapping;
        return updated;
      }
      return [...prev, newMapping];
    });
  }, [store]);

  const removeMapping = useCallback(async (calendarId: string): Promise<void> => {
    if (!store) {
      throw new Error('Storage not ready');
    }
    await store.removeMapping(calendarId);
    setMappings(prev => prev.filter(m => m.calendarId !== calendarId));
  }, [store]);

  return {
    mappings,
    isLoading,
    error,
    refresh: loadMappings,
    getMappingForCalendar,
    setMapping,
    removeMapping,
    getMemberIdsForCalendar,
  };
}
